import { Emit, Inject, Options, Prop, Vue, Watch } from "vue-property-decorator";
import { TrustAPI } from "@/shared/modules/trust-api";
import { AppInsights } from "@/trust-atcs-app/appInsights";
import { EventEmitter } from "events";
import store from "@/trust-atcs-app/store";
import FormatDateTimeMixin from '@/shared/mixins/format-datetime';
import LoadingSpinner from "@/shared/components/loading-spinner/index.vue";
import { FormPartBData } from "@/trust-atcs-app/models/form-part-b-data";
import RadioButtonHorizontal from "../questions/radiobutton-horizontal/index.vue";
import RadioButtonVertical from "../questions/radiobutton-vertical/index.vue";
import RadioButtonBranchList from "../questions/radiobutton-branchlist/index.vue";
import RadioButtonSubQuestions from "../questions/radiobutton-subquestions/index.vue";
import { ComplianceForm, PartBFormGeneralTrustMoney, PartBFormTrustAccountingRecords } from "@/shared/modules/typescript-trustapi-client/models";
import BankAccount from "../questions/bank-account/index.vue";
import FileUploader from "../questions/file-uploader/index.vue";
import Signatories from "../questions/signatories/index.vue";
import { QuestionType } from '../questions/question-type';
import RadioButtonWithFiles from "../questions/radiobutton-with-files/index.vue";

@Options({
  components: {
    LoadingSpinner,
    RadioButtonVertical,
    RadioButtonHorizontal,
    RadioButtonBranchList,
    RadioButtonSubQuestions,
    BankAccount,
    Signatories,
    FileUploader,
    RadioButtonWithFiles,
  },
  mixins: [FormatDateTimeMixin],
})
export default class FormPartBGeneralTrustMoney extends Vue {
  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  @Prop({ default: false })
  private isSectionLoading!: boolean;

  @Prop()
  private formPartBData!: FormPartBData;

  @Prop()
  private isSaving!: boolean;

  @Emit('update:isSectionLoading')
  @Emit('validForm')

  @Watch('loadingContent')
  private sectionLoading() {
    this.$emit('update:isSectionLoading', this.loadingContent);
  }

  private ai = new AppInsights;
  private loadingContent = false;
  private errorLoadingContent = false;

  private validMainQuestions: boolean[] = [];
  private generalTrustMoney: PartBFormGeneralTrustMoney = null as any;

  mounted(): void {
    this.eventCtrl.on('reload-form-b-gtm-content', this.loadGeneralTrustMoneyContent);
    this.loadingContent = true;
    this.loadGeneralTrustMoneyContent(true);
    this.$emit('validForm', false);
  }

  beforeUnmount(): void {
    this.eventCtrl.removeListener('reload-form-b-gtm-content', this.loadGeneralTrustMoneyContent);
  }

  private async loadGeneralTrustMoneyContent(firstLoad?: boolean): Promise<void> {
    try {
      // this.loadingContent = true;
      const resp: any = await this.apiService.PartBFormApi?.apiV2PartBFormGetGeneralTrustMoneyPost({
        complianceFormId: this.complianceFormB.id,
        updatingKey: this.updatingKey,
        manageSignatoryUpdatingKey: this.signatoriesUpdatingKey,
        createSignatorySnapshot: true,
      });
      this.generalTrustMoney = resp.data.data as PartBFormTrustAccountingRecords;
      this.formPartBData.generalTrustMoney = this.generalTrustMoney as any;

      // Sort atcsMainQuestions by order number
      this.formPartBData.generalTrustMoney.atcsMainQuestions = this.formPartBData.generalTrustMoney.atcsMainQuestions.sort((a: any, b: any) => a.order - b.order);

      this.formPartBData.generalTrustMoney.atcsMainQuestions.forEach((q: any) => {
        q.responses = q.responses.sort((a: any, b: any) => a.order - b.order);
      });

      // Sort atcsSubQuestions by order number
      this.formPartBData.generalTrustMoney.atcsMainQuestions.forEach((q: any) => {
        q.atcsSubQuestions = q.atcsSubQuestions.sort((a: any, b: any) => a.order - b.order);
      });

      if (firstLoad) {
        this.validMainQuestions = this.formPartBData.generalTrustMoney.atcsMainQuestions.map((q: any) => false);
      }
      // add valid flag to each bank account
      this.formPartBData.generalTrustMoney.atcsMainQuestions.forEach((q: any, i: number) => {

        if (q.atcsQuestionTypeId == QuestionType.PerBankAccount || q.atcsQuestionTypeId == QuestionType.PerBankAccountV2) {
          this.formPartBData.generalTrustMoney.atcsMainQuestions[i].formPracticeAccountQuestions =
            this.formPartBData.generalTrustMoney.atcsMainQuestions[i].formPracticeAccountQuestions ?? [];
        }

        if (q.atcsQuestionTypeId == 20) {
          this.formPartBData.generalTrustMoney.atcsMainQuestions[i].formPracticeSignatoryQuestions =
            this.formPartBData.generalTrustMoney.atcsMainQuestions[i].formPracticeSignatoryQuestions ?? [];
        }

        if (q.formPracticeAccountQuestions) {
          q.formPracticeAccountQuestions.forEach((b: any) => {
            b.valid = this.isValidBankAccount(b);
          });
        }
      });
      // add valid flag to each signatory
      // this.formPartBData.generalTrustMoney.atcsMainQuestions.forEach((q: any) => {
      //   if (q.formPracticeSignatoryQuestions) {
      //     q.formPracticeSignatoryQuestions.forEach((s: any) => {
      //       s.valid = this.isValidSignatory(s);
      //     });
      //   }
      // });
    } catch (err) {
      this.eventCtrl.emit('show-snackbar', 'error', 'Error getting General Trust Money');
      this.ai.trackException('Error getting General Trust Money: ' + err);
      this.errorLoadingContent = true;
      console.log(err);
    } finally {
      this.loadingContent = false;
    }
  }

  private validForm(): boolean {
    const allTrue = Object.values(this.validMainQuestions).every(value => value === true);
    return allTrue;
  }

  private validationForm(i: any, isValid: any) {
    this.validMainQuestions[i] = isValid;
    this.$emit('validForm', this.validForm());
  }

  get complianceFormB(): ComplianceForm {
    return store.getters.getSelectedComplianceFormB;
  }

  get updatingKey(): string {
    return store.getters.getFormUpdatingKey;
  }

  // private isValidSignatory(signatory: any): boolean {
  //   if (signatory) {
  //     signatory.valid = false;
  //     const firstName = signatory?.firstName?.length > 0;
  //     const lastName = signatory?.lastName?.length > 0;
  //     const categoryId = signatory?.categoryId > 0;
  //     const commencedDate = signatory?.commencedDate !== null;
  //     const solelyJointlyId = signatory?.solelyJointlyId > 0;
  //     if (firstName && lastName && categoryId && solelyJointlyId && commencedDate) {
  //       return true;
  //     }
  //   }
  //   return false;
  // }

  private isValidBankAccount(bank: any): boolean {
    if (bank) {
      bank.valid = false;
      for (let i = 0; i < bank.atcsSubQuestions.length; i++) {
        if (bank.atcsQuestionTypeId == 5 && bank.atcsSubQuestions.responses[0].answerCurrency == null) {
          return false;
          break;
        }
      }
    }
    return false;
  }

  get QuestionType(): any {
    return QuestionType;
  }

  get signatoriesPracticeCoreId(): number | null {
    return store.getters.getSelectedPracticeCoreId;
  }

  get signatoriesUpdatingKey(): string {
    return store.getters.getSignatoriesUpdatingKey;
  }

}

